import { useMemo, useRef } from 'react';
import { type NavMesh, NavMeshQuery, QueryFilter } from '@recast-navigation/core';
import * as THREE from 'three';
import { NavMeshHelper } from '../three/NavMeshHelper';
import { useMutableCallback } from '@slope/hooks';

export type UseNavMeshRaycastOptions = {
  /**
   * The half extents of the area to query for the closest point on the nav‑mesh.
   * If not provided, the query will use the default half extents of the nav‑mesh.
   */
  halfExtents?: THREE.Vector3Like;

  /**
   * The flags to include in the query filter.
   * If not provided, all flags will be included.
   */
  flags?: number;
};

/**
 * Returns a stable function that converts the current pointer position into a world‑space point on – or
 * snapped to – the supplied nav‑mesh. The hook is completely self‑contained: it creates its own
 * `Raycaster`, `NavMeshQuery`, helper surface, and query filter.
 */
export function useNavMeshRaycast(navMesh: NavMesh, options?: UseNavMeshRaycastOptions) {
  const { halfExtents, flags } = options || {};
  const surface = useMemo(() => new NavMeshHelper(navMesh), [navMesh]);
  const query = useMemo(() => new NavMeshQuery(navMesh), [navMesh]);
  const filter = useMemo(() => {
    if (!flags) return undefined;
    const f = new QueryFilter();
    f.includeFlags = flags;
    return f;
  }, [flags]);

  const raycasterRef = useRef<THREE.Raycaster>(null);
  if (!raycasterRef.current) {
    const rc = new THREE.Raycaster();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rc as any).firstHitOnly = true;
    raycasterRef.current = rc;
  }

  const project = useMutableCallback(
    (
      pointer: THREE.Vector2,
      camera: THREE.Camera,
      target?: THREE.Vector3,
    ): THREE.Vector3 | null => {
      if (!raycasterRef.current) {
        return null;
      }
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(pointer, camera);

      // Try to hit the nav‑mesh helper first (fast path)
      const hit = raycaster.intersectObject(surface, true)[0]?.point;
      const projected = hit ? hit.clone() : new THREE.Vector3();

      // Fallback: shoot against the XZ plane (y = 0)
      if (!hit) {
        const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        raycaster.ray.intersectPlane(ground, projected);
      }

      if (projected.length() === 0) return null;

      // Snap to nearest polygon so we stay inside the nav‑mesh
      const match = query.findClosestPoint(projected, {
        halfExtents,
        filter,
      });

      if (match?.point) {
        projected.copy(match.point);
      }

      return target ? target.copy(projected) : projected;
    },
  );

  return project;
}
